import React, {
    useEffect,
    useState,
    useCallback
} from "react";

import {
    Table,
    Button,
    Card,
    Typography,
    Space,
    Tag,
    Input,
    Empty,
    Row,
    Col,
    Statistic
} from "antd";
import { useAcademicYear } from "../../AcademicYearContext";

import {
    ArrowUpOutlined,
    UserOutlined,
    SearchOutlined,
    HistoryOutlined,
    TeamOutlined
} from "@ant-design/icons";

import {
    motion
} from "framer-motion";

import Swal from "sweetalert2";

import api from "../../api/api";

import PromotionStudentRecord from "./PromotionStudentRecord";
import PromotionHistoryPDF from "./PromotionHistoryPDF";

import "./PromotionDashboard.css";


const {
    Title,
    Text
} = Typography;


const PromotionDashboard = () => {

    const { selectedYear } = useAcademicYear();
    const [students, setStudents] = useState([]);

    const [loading, setLoading] = useState(false);

    const [search, setSearch] = useState("");

    const [totalStudents, setTotalStudents] = useState(0);

    const [currentPage, setCurrentPage] = useState(1);

    const [pageSize, setPageSize] = useState(50);

    const [selectedStudent, setSelectedStudent] = useState(null);

    const [openPromotion, setOpenPromotion] = useState(false);

    const [openHistory, setOpenHistory] = useState(false);



    // =====================================
    // LOAD STUDENTS
    // =====================================

    const fetchStudents = useCallback(
        async (
            page = 1,
            size = pageSize,
            keyword = search
        ) => {

       
            try {
                if(!selectedYear?.id){
                    return;
                }
                setLoading(true);


                let url =
                    `/api/users/students/?page=${page}&page_size=${size}&academic_year=${selectedYear?.id}`;


                if(keyword.trim()) {

                    url +=
                    `&search=${encodeURIComponent(keyword)}`;

                }


    const response = await api.get(url);
    const data = response.data.results || [];
    setStudents(data);
    setTotalStudents(response.data.count || data.length);
    setCurrentPage(response.data.current_page || page);

            }
            catch(error) {


                console.log(error);


                Swal.fire({

                    icon:"error",

                    title:"Error",

                    text:"Unable to load students."

                });


            }
            finally {

                setLoading(false);

            }


        },
        [
            pageSize,
            search,
            selectedYear
        ]
    );



    // =====================================
    // REFRESH AFTER PROMOTION
    // =====================================

    const refreshStudents = () => {

        fetchStudents(

            currentPage,

            pageSize,

            search

        );

    };



    // =====================================
    // INITIAL LOAD
    // =====================================

    useEffect(() => {

        fetchStudents();

    }, [fetchStudents]);




    // =====================================
    // SEARCH
    // =====================================

    useEffect(() => {


        const timer =
            setTimeout(() => {


                fetchStudents(

                    1,

                    pageSize,

                    search

                );


            },700);



        return () =>
            clearTimeout(timer);


}, [
    search,
    selectedYear
]);




    // =====================================
    // PAGINATION
    // =====================================

    const handleTableChange = (pagination) => {


        setPageSize(

            pagination.pageSize

        );



        fetchStudents(

            pagination.current,

            pagination.pageSize,

            search

        );


    };




    // =====================================
    // OPEN PROMOTION
    // =====================================

    const openPromotionModal = (student) => {


        setSelectedStudent(student);

        setOpenPromotion(true);


    };




    // =====================================
    // OPEN HISTORY
    // =====================================

    const openHistoryModal = async(student)=>{


        try {


            const studentId =
                student.user_id;



            const response =
                await api.get(

                    `/student-promotion-history/${studentId}/`

                );



            setSelectedStudent({

                ...student,

                promotion_history:
                    response.data

            });



            setOpenHistory(true);


        }
        catch(error){

            console.log(error);

        }


    };





    // =====================================
    // TABLE COLUMNS
    // =====================================

    const columns = [


        {
            title:"Student",

            key:"student",

            width:220,

            render:(record)=>(

                <Space>

                    <UserOutlined />

                    <Text strong>

                        {record.full_name}

                    </Text>

                </Space>

            )

        },



        {
            title:"Current Class",

            key:"class",

            render:(record)=>(

                <Tag color="blue">

                    {
                        record.grade_class_name || "-"
                    }

                </Tag>

            )

        },



        {
            title:"Division",

            key:"division",

            render:(record)=>(

                <Text>

                    {
                        record.division_assigned_name || "-"
                    }

                </Text>

            )

        },



        {
            title:"History",

            key:"history",

            render:(record)=>(

                <Button

                    icon={<HistoryOutlined />}

                    onClick={() =>
                        openHistoryModal(record)
                    }

                >

                    History

                </Button>

            )

        },



        {
            title:"Action",

            key:"action",

            render:(record)=>(

                <Button

                    type="primary"

                    icon={<ArrowUpOutlined />}

                    onClick={() =>
                        openPromotionModal(record)
                    }

                >

                    Promote

                </Button>

            )

        }


    ];




    return (

        <motion.div

            initial={{
                opacity:0,
                y:30
            }}

            animate={{
                opacity:1,
                y:0
            }}

        >


            <Card

                style={{
                    borderRadius:18
                }}

            >


                <Title level={3}>

                    Student Promotion

                </Title>



                <Text type="secondary">

                    Promote students into the next academic year.

                </Text>



                <Row

                    gutter={[16,16]}

                    style={{
                        marginTop:25,
                        marginBottom:20
                    }}

                >


                    <Col xs={24} md={8}>


                        <Card>


                            <Statistic

                                title="Total Students"

                                value={totalStudents}

                                prefix={<TeamOutlined />}

                            />


                        </Card>


                    </Col>


                </Row>





                <Input


                    placeholder="Search student name..."


                    prefix={<SearchOutlined />}


                    allowClear


                    value={search}


                    onChange={(e)=>

                        setSearch(e.target.value)

                    }


                    style={{

                        maxWidth:350,

                        marginBottom:20

                    }}


                />





                <Table


                    className="promotion-table"


                    columns={columns}


                    dataSource={students}


                    loading={loading}


                    rowKey={(record)=>

                        record.user_id || record.id

                    }


                    scroll={{
                        x:800
                    }}



                    locale={{

                        emptyText:

                        <Empty description="No students found"/>

                    }}




                    pagination={{


                        current:currentPage,


                        pageSize:pageSize,


                        total:totalStudents,


                        showSizeChanger:true,


                        pageSizeOptions:[

                            "20",
                            "50",
                            "100",
                            "200"

                        ],



                        showTotal:(total)=>

                            `Total ${total} students`


                    }}



                    onChange={handleTableChange}



                />







                <PromotionStudentRecord


                    open={openPromotion}


                    student={selectedStudent}



                    onClose={()=>{


                        setOpenPromotion(false);

                        setSelectedStudent(null);


                    }}




                    refresh={refreshStudents}



                />







                <PromotionHistoryPDF


                    open={openHistory}


                    student={selectedStudent}



                    onClose={()=>{


                        setOpenHistory(false);

                        setSelectedStudent(null);


                    }}


                />



            </Card>


        </motion.div>


    );


};


export default PromotionDashboard;